/**
 * Bidirectional example-beer solver for Pricing Guide.
 * Last-edited field is the anchor; the rest re-forward through existing engines.
 * Optional locked field stays stamped after reverse-from-slider.
 */

import {
  isKegSku,
  isPackSku,
  isLooseSku,
  calculateProgressivePrice,
  priceFieldGreyOut,
} from 'util/pricing-fields';
import {
  GST_MULT,
  parseCaseUnits,
  kegVolumeMlFromSku,
  resolveTargetEntry,
  getUnitCostExGstForField,
  computeMargin,
} from './marginUtils';
import {
  applyRounding,
  targetPctToDivisor,
  isHighAbvOrWild,
  parseAbv,
  getKegSuggestionsForSku,
  roundToNearest50c,
  KEG_FALLBACK_DIVISORS,
  KEG_POUR_VOLUME_L,
  KEG_POUR_FIELDS,
  PINT_MAX_ABV,
} from './kegPricingSuggestions';
import {
  calculateRrpSuggestions,
  calculateOpSuggestion,
  calculateProgressiveRetailSuggestions,
  unitRrpFromSlabCase,
} from './pricingSuggestions';

const UPLIFT_POUR_FIELDS = new Set([
  'priceVenuePerLitre',
  'pricePint',
  'priceHalfPint',
  'price150ml',
]);

const RRP_FALLBACK_PCT = {
  rrpUnit: 28,
  rrpPack: 25,
  rrpCase: 28,
};

const KEG_POUR_L = {
  priceVenuePerLitre: 1,
  pricePint: KEG_POUR_VOLUME_L.pricePint,
  priceHalfPint: KEG_POUR_VOLUME_L.priceHalfPint,
  price150ml: KEG_POUR_VOLUME_L.price150ml,
};

/** Format chips for Example beer — mirrors Product Editor SKU presets. */
export const EXAMPLE_FORMAT_PRESET_GROUPS = [
  {
    key: 'can',
    presets: [
      {
        key: '15x440',
        label: '15 × 440ml',
        values: {
          containerType: 'can', unitType: '440', packType: '1', caseType: '15x1',
        },
      },
      {
        key: '24x330',
        label: '24 × 330ml',
        values: {
          containerType: 'can', unitType: '330', packType: '1', caseType: '24x1',
        },
      },
      {
        key: '24x440',
        label: '24 × 440ml',
        values: {
          containerType: 'can', unitType: '440', packType: '1', caseType: '24x1',
        },
      },
      {
        key: '4x6-330',
        label: '4 × 6 330ml',
        values: {
          containerType: 'can', unitType: '330', packType: '6', caseType: '4x6',
        },
      },
    ],
  },
  {
    key: 'keg',
    presets: [
      {
        key: '19.5L',
        label: '19.5L keg',
        values: {
          containerType: 'keg', unitType: '19500', packType: '1', caseType: '1x1',
        },
      },
      {
        key: '30L',
        label: '30L keg',
        values: {
          containerType: 'keg', unitType: '30000', packType: '1', caseType: '1x1',
        },
      },
      {
        key: '50L',
        label: '50L keg',
        values: {
          containerType: 'keg', unitType: '50000', packType: '1', caseType: '1x1',
        },
      },
    ],
  },
  {
    key: 'bottle',
    presets: [
      {
        key: '12x375',
        label: '375ml × 12 bottle',
        values: {
          containerType: 'bottle', unitType: '375', packType: '1', caseType: '12x1',
        },
      },
      {
        key: '6x750',
        label: '750ml × 6 bottle',
        values: {
          containerType: 'bottle', unitType: '750', packType: '1', caseType: '6x1',
        },
      },
    ],
  },
];

export const EXAMPLE_FORMAT_PRESETS = EXAMPLE_FORMAT_PRESET_GROUPS
  .flatMap((g) => g.presets);

export function buildExampleSku({
  containerType, unitType, packType, caseType, productRange, productType,
}) {
  return {
    containerType,
    unitType,
    packType,
    caseType,
    productRange: productRange === '*' ? null : productRange,
    productType: productType || '',
  };
}

function parseNum(v) {
  if (v == null || v === '') return null;
  const n = parseFloat(String(v));
  return Number.isNaN(n) || n <= 0 ? null : n;
}

function round2(n) {
  if (n == null || Number.isNaN(n)) return null;
  return Math.round(n * 100) / 100;
}

/**
 * Packaged Off→On dollar uplift.
 * 330ml cans +$6; 440ml cans + bottle cases +$8. Kegs: 0.
 */
export function onPremCaseUplift(sku, rules) {
  if (!sku || isKegSku(sku)) return 0;
  const ct = String(sku.containerType ?? sku.container_type ?? '').toLowerCase();
  const ut = String(sku.unitType ?? sku.unit_type ?? '').toLowerCase();
  if (ct === 'bottle') {
    const n = parseFloat(rules?.on_prem_case_uplift_440_bottle ?? 8);
    return Number.isNaN(n) ? 8 : n;
  }
  if (ut === '330' || ut === '330ml' || ut === '330b') {
    const n = parseFloat(rules?.on_prem_case_uplift_330 ?? 6);
    return Number.isNaN(n) ? 6 : n;
  }
  const n = parseFloat(rules?.on_prem_case_uplift_440_bottle ?? 8);
  return Number.isNaN(n) ? 8 : n;
}

export function onPremFromOff(off, sku, rules) {
  const offN = parseNum(off);
  if (offN == null) return null;
  return round2(offN + onPremCaseUplift(sku, rules));
}

export function offPremFromOn(on, sku, rules) {
  const onN = parseNum(on);
  if (onN == null) return null;
  return round2(onN - onPremCaseUplift(sku, rules));
}

function skuRange(sku) {
  return sku?.productRange ?? sku?.product_range ?? null;
}

function costFromSell(sellIncGst, targetPct) {
  const d = targetPctToDivisor(targetPct);
  if (d == null || d <= 0 || sellIncGst == null || Number.isNaN(sellIncGst) || sellIncGst <= 0) {
    return null;
  }
  return (sellIncGst / GST_MULT) * d;
}

function targetPctForRrp(targetMap, sku, camelField) {
  const entry = resolveTargetEntry(targetMap, skuRange(sku), camelField, '');
  if (entry?.pct != null) return entry.pct;
  return RRP_FALLBACK_PCT[camelField] ?? null;
}

function offFromRrpField(sku, sell, camelField, targetMap) {
  const sellN = parseNum(sell);
  const pct = targetPctForRrp(targetMap, sku, camelField);
  const cost = costFromSell(sellN, pct);
  if (cost == null) return null;
  if (camelField === 'rrpCase') return round2(cost);
  const parsed = parseCaseUnits(sku?.caseType ?? sku?.case_type);
  if (!parsed) return null;
  if (camelField === 'rrpUnit') {
    if (parsed.totalUnits <= 0) return null;
    return round2(cost * parsed.totalUnits);
  }
  if (camelField === 'rrpPack') {
    if (parsed.casePacks <= 0) return null;
    return round2(cost * parsed.casePacks);
  }
  return null;
}

function opDivisor(sku, rules) {
  const unit = String(sku.unitType ?? sku.unit_type ?? '');
  const type = String(sku.productType ?? sku.product_type ?? '');
  let divisor = parseFloat(rules?.op_divisor_330 ?? 0.32);
  if (unit === '440' || unit === '440ml') {
    divisor = parseFloat(rules?.op_divisor_440 ?? 0.45);
  }
  if (type === 'Wild' || type === 'Clean Barrel' || type === 'Other') {
    divisor = parseFloat(rules?.op_divisor_specialty ?? 0.50);
  }
  return divisor > 0 ? divisor : null;
}

function offFromOp(sku, op, rules) {
  const opN = parseNum(op);
  const d = opDivisor(sku, rules);
  const parsed = parseCaseUnits(sku?.caseType ?? sku?.case_type);
  if (opN == null || d == null || !parsed || parsed.totalUnits <= 0) return null;
  const unitCost = (opN / GST_MULT) * d;
  return round2(unitCost * parsed.totalUnits);
}

function progressiveMult(rules) {
  const raw = parseFloat(rules?.progressive_unit_markup ?? 0.0265);
  if (Number.isNaN(raw) || raw <= 0) return 1.0265;
  if (raw > 1 && raw < 1.5) return raw;
  if (raw >= 1.5) return 1 + raw / 100;
  return 1 + raw;
}

function offFromProgressiveTrade(sku, progressive) {
  const p = parseNum(progressive);
  if (p == null) return null;
  const ct = String(sku?.caseType ?? sku?.case_type ?? '');
  if (ct === '4x6') return round2(p);
  return round2(p / 1.0265);
}

function kegDivisor(sku, field, abv, targetMap, rules) {
  const high = isHighAbvOrWild(abv, sku.productType ?? sku.product_type, rules);
  const cond = high && UPLIFT_POUR_FIELDS.has(field) ? 'high_abv_or_wild' : '';
  const entry = resolveTargetEntry(targetMap, skuRange(sku), field, cond);
  const fromTarget = targetPctToDivisor(entry?.pct);
  if (fromTarget != null && fromTarget > 0) return fromTarget;
  return KEG_FALLBACK_DIVISORS[field];
}

function onFromKegPour(sku, field, pour, abv, targetMap, rules) {
  const pourN = parseNum(pour);
  const pourL = KEG_POUR_L[field];
  const d = kegDivisor(sku, field, abv, targetMap, rules);
  if (pourN == null || pourL == null || !d) return null;
  const volL = kegVolumeMlFromSku(sku) / 1000;
  if (volL <= 0) return null;
  const kegPL = ((pourN / GST_MULT) * d) / pourL;
  return round2(kegPL * volL);
}

function pintSkipped(abv) {
  const abvNum = parseAbv(abv);
  return abvNum != null && abvNum > PINT_MAX_ABV;
}

function fillTradePair(sku, priceOff, priceOn, rules, prefer) {
  if (isKegSku(sku)) {
    const on = priceOn ?? priceOff;
    return { priceOff: on, priceOn: on };
  }
  let off = priceOff;
  let on = priceOn;
  if (prefer === 'off' && off != null && on == null) {
    on = onPremFromOff(off, sku, rules);
  } else if (prefer === 'on' && on != null && off == null) {
    off = offPremFromOn(on, sku, rules);
  } else if (off != null && on == null) {
    on = onPremFromOff(off, sku, rules);
  } else if (on != null && off == null) {
    off = offPremFromOn(on, sku, rules);
  }
  return { priceOff: off, priceOn: on };
}

function forwardPackaged(sku, priceOff, priceOn, targetMap, rules) {
  const prices = { priceOff, priceOn };
  const rrp = calculateRrpSuggestions(sku, prices, targetMap, rules);
  const merged = { ...prices, ...rrp };
  const op = calculateOpSuggestion(sku, merged, rules);
  const progRetail = calculateProgressiveRetailSuggestions(
    sku, { ...merged, rrpUnit: rrp.rrpUnit }, targetMap, rules,
  );
  const progressive = calculateProgressivePrice(priceOff, sku);
  const onN = parseNum(priceOn);
  return {
    priceOff,
    priceOn,
    priceProgressive: progressive != null ? round2(progressive) : null,
    corporate: onN != null ? Math.round(onN * 1.1) : null,
    rrpUnit: rrp.rrpUnit,
    rrpPack: rrp.rrpPack,
    rrpCase: rrp.rrpCase,
    priceBar: op,
    progressiveUnit: progRetail.progressiveUnit,
    progressivePack: progRetail.progressivePack,
    priceVenuePerLitre: null,
    pricePint: null,
    priceHalfPint: null,
    price150ml: null,
  };
}

function forwardKeg(sku, priceOn, abv, targetMap, rules) {
  const sug = getKegSuggestionsForSku(sku, priceOn, abv, targetMap, {
    productType: sku.productType, rules,
  });
  return {
    priceOff: priceOn,
    priceOn,
    priceProgressive: null,
    corporate: null,
    rrpUnit: null,
    rrpPack: null,
    rrpCase: null,
    priceBar: null,
    progressiveUnit: null,
    progressivePack: null,
    priceVenuePerLitre: sug?.priceVenuePerLitre ?? null,
    pricePint: sug?.pricePint ?? null,
    priceHalfPint: sug?.priceHalfPint ?? null,
    price150ml: sug?.price150ml ?? null,
  };
}

export function exampleFieldGreyed(sku, fieldKey) {
  if (fieldKey === 'corporate' || fieldKey === 'rrpUnit') return isKegSku(sku);
  if (fieldKey === 'progressiveUnit') {
    return isKegSku(sku) || isPackSku(sku);
  }
  if (fieldKey === 'progressivePack') {
    return isKegSku(sku) || isLooseSku(sku);
  }
  return priceFieldGreyOut(sku, fieldKey);
}

/**
 * @param {object} args
 * @param {string} [args.lockedField]
 * @param {number|string} [args.lockedValue]
 * @returns {{
 *   prices: object,
 *   impliedFrom: string|null,
 *   skipped: object,
 *   notes: object,
 * }}
 */
export function solveExamplePrices({
  sku, anchorField, anchorValue, targetMap, rules, abv,
  lockedField, lockedValue,
}) {
  const skipped = {};
  const notes = {};
  const empty = {
    prices: {},
    impliedFrom: null,
    skipped,
    notes,
  };
  if (!sku) return empty;
  const anchorN = parseNum(anchorValue);
  if (anchorN == null) return empty;

  let priceOff = null;
  let priceOn = null;
  let prefer = null;
  let impliedFrom = null;

  const lockN = parseNum(lockedValue);
  const lockLive = Boolean(
    lockedField
    && lockN != null
    && !exampleFieldGreyed(sku, lockedField),
  );

  if (isKegSku(sku) && anchorField === 'pricePint' && pintSkipped(abv)) {
    skipped.pricePint = `ABV over ${PINT_MAX_ABV}`;
    return {
      prices: {}, impliedFrom: null, skipped, notes,
    };
  }

  if (isKegSku(sku)) {
    if (KEG_POUR_FIELDS.includes(anchorField)) {
      priceOn = onFromKegPour(sku, anchorField, anchorN, abv, targetMap, rules);
      impliedFrom = 'priceOn';
    } else {
      priceOn = anchorN;
    }
    priceOff = priceOn;
  } else if (anchorField === 'priceOff') {
    priceOff = anchorN;
    prefer = 'off';
  } else if (anchorField === 'priceOn') {
    priceOn = anchorN;
    prefer = 'on';
  } else if (anchorField === 'corporate') {
    priceOn = round2(anchorN / 1.1);
    prefer = 'on';
    impliedFrom = 'priceOn';
  } else if (anchorField === 'priceProgressive') {
    priceOff = offFromProgressiveTrade(sku, anchorN);
    prefer = 'off';
    impliedFrom = 'priceOff';
  } else if (anchorField === 'priceBar') {
    priceOff = offFromOp(sku, anchorN, rules);
    prefer = 'off';
    impliedFrom = 'priceOff';
  } else if (anchorField === 'rrpUnit' || anchorField === 'rrpPack' || anchorField === 'rrpCase') {
    if (anchorField === 'rrpCase') {
      const unitRrp = unitRrpFromSlabCase(sku, anchorN, rules);
      priceOff = offFromRrpField(sku, unitRrp, 'rrpUnit', targetMap);
    } else {
      priceOff = offFromRrpField(sku, anchorN, anchorField, targetMap);
    }
    prefer = 'off';
    impliedFrom = 'priceOff';
  } else if (anchorField === 'progressiveUnit') {
    const mult = progressiveMult(rules);
    const rrpUnit = round2(anchorN / mult);
    priceOff = offFromRrpField(sku, rrpUnit, 'rrpUnit', targetMap);
    prefer = 'off';
    impliedFrom = 'priceOff';
  } else if (anchorField === 'progressivePack') {
    priceOff = offFromRrpField(sku, anchorN, 'rrpPack', targetMap);
    prefer = 'off';
    impliedFrom = 'priceOff';
  } else if (KEG_POUR_FIELDS.includes(anchorField)) {
    return empty;
  } else {
    return empty;
  }

  const trade = fillTradePair(sku, priceOff, priceOn, rules, prefer);
  const prices = isKegSku(sku)
    ? forwardKeg(sku, trade.priceOn, abv, targetMap, rules)
    : forwardPackaged(sku, trade.priceOff, trade.priceOn, targetMap, rules);

  if (pintSkipped(abv)) {
    skipped.pricePint = `ABV over ${PINT_MAX_ABV}`;
    prices.pricePint = null;
  }

  prices[anchorField] = anchorN;
  if (lockLive && lockedField !== anchorField && !skipped[lockedField]) {
    prices[lockedField] = lockN;
  }

  if (!isKegSku(sku) && prices.priceOn != null && prices.priceOff != null) {
    const uplift = onPremCaseUplift(sku, rules);
    notes.onPremMarkup = `On Prem = Off Prem + $${uplift}`;
  }

  return {
    prices, impliedFrom, skipped, notes,
  };
}

export function exampleFieldMargin(fieldKey, sku, prices) {
  if (!sku || !prices) return null;
  const { displayPct } = computeMargin(fieldKey, sku, prices);
  return displayPct;
}

export function exampleFieldCost(fieldKey, sku, prices) {
  return getUnitCostExGstForField(sku, fieldKey, prices);
}

const SPECIALTY_GUIDE_TYPES = new Set(['Wild', 'Clean Barrel', 'Other']);

/** Playground Type: specialty stays; Beer and other non-specialty → Standard. */
export function guideTypeFromProduct(type) {
  const t = type != null ? String(type).trim() : '';
  return SPECIALTY_GUIDE_TYPES.has(t) ? t : '';
}

function formatPart(sku, camel) {
  const snake = camel.replace(/[A-Z]/g, (ch) => `_${ch.toLowerCase()}`);
  return String(sku?.[camel] ?? sku?.[snake] ?? '').trim().toLowerCase();
}

/** Full playground format: container + unit + pack + case. */
export function formatFieldsEqual(a, b) {
  if (!a || !b) return false;
  return ['containerType', 'unitType', 'packType', 'caseType']
    .every((key) => formatPart(a, key) === formatPart(b, key));
}

export function isActiveSku(sku) {
  if (!sku) return false;
  return !sku.deleted;
}

export function findSkuByFormat(skus, formatValues) {
  return (skus || []).find(
    (s) => isActiveSku(s) && formatFieldsEqual(s, formatValues),
  ) || null;
}

export function skuHasFormat(skus, formatValues) {
  return !!findSkuByFormat(skus, formatValues);
}

/** Not SKU columns — playground-only. */
export const APPLY_SKIP_FIELDS = new Set([
  'corporate',
  'progressiveUnit',
  'progressivePack',
]);

/** Trade progressive stays to the cent — never 50¢. */
export const APPLY_NO_50C_FIELDS = new Set([
  'priceProgressive',
]);

export function roundApplyPricesTo50c(prices) {
  const next = {};
  Object.keys(prices || {}).forEach((key) => {
    if (APPLY_NO_50C_FIELDS.has(key)) {
      next[key] = prices[key];
      return;
    }
    const rounded = roundToNearest50c(Number(prices[key]));
    if (rounded != null) next[key] = rounded;
  });
  return next;
}

function formatPriceMoney(n) {
  if (n == null || Number.isNaN(Number(n))) return '';
  const v = Number(n);
  return Number.isInteger(v) ? String(v) : v.toFixed(2);
}

/** Numeric price from camel or snake; blank / non-numeric → null. */
export function skuPriceValue(sku, camelKey) {
  if (!sku || !camelKey) return null;
  const snake = camelKey.replace(/[A-Z]/g, (ch) => `_${ch.toLowerCase()}`);
  const raw = sku[camelKey] ?? sku[snake];
  if (raw == null || String(raw).trim() === '') return null;
  const n = Number(raw);
  return Number.isNaN(n) ? null : n;
}

export function currentPriceNote(sku, camelKey) {
  const value = skuPriceValue(sku, camelKey);
  if (value == null) return null;
  return `(Current price $${formatPriceMoney(value)})`;
}

/** Apply payload: only persistable keys whose number differs from the live SKU. */
export function changedPrices(applyPrices, existingSku) {
  const next = {};
  Object.keys(applyPrices || {}).forEach((key) => {
    if (APPLY_SKIP_FIELDS.has(key)) return;
    if (applyPrices[key] == null || applyPrices[key] === '') return;
    const applyN = Number(applyPrices[key]);
    if (Number.isNaN(applyN)) return;
    const current = skuPriceValue(existingSku, key);
    if (current != null && current === applyN) return;
    next[key] = applyN;
  });
  return next;
}

export function persistableFieldGroups(groups) {
  return (groups || [])
    .map((group) => ({
      ...group,
      fields: group.fields.filter((f) => !APPLY_SKIP_FIELDS.has(f.key)),
    }))
    .filter((group) => group.fields.length);
}
